import { Order, orderFromJSONLegacy } from '@exodus/models';
import { oldToNewStyleTokenNames } from '@exodus/asset-legacy-token-name-mapping';
export const isOrderSupported = ({ order, availableAssetNames, }) => !!order.fromAmount &&
    !!order.toAmount &&
    availableAssetNames.includes(order.fromAsset ?? '') &&
    availableAssetNames.includes(order.toAsset ?? '');
export const isOrderMetadataValid = (order) => {
    if (!Array.isArray(order.txIds))
        return false;
    return order.txIds.length === 0 || typeof order.txIds[0] === 'object';
};
export const convertLegacyOrderMetadata = (order) => {
    const txIds = order.txIds;
    if (typeof txIds?.[0] === 'string') {
        const stringTxIds = txIds.filter((txId) => typeof txId === 'string');
        const normalizedTxIds = stringTxIds.map((txId) => ({
            txId,
            txType: 'swapTransaction',
        }));
        return order.update({
            txIds: normalizedTxIds,
        });
    }
    if (!order.fromTxId) {
        return order;
    }
    const txIdsWithTypes = [
        { txId: order.fromTxId, txType: 'depositTransaction' },
        ...(order.toTxId ? [{ txId: order.toTxId, txType: 'payoutTransaction' }] : []),
    ];
    return order.update({
        txIds: txIdsWithTypes,
    });
};
const mapOrdersToWalletNames = ({ orders, assetsModule, }) => {
    return orders.map((order) => {
        const currentFromAsset = order.fromAsset;
        const currentToAsset = order.toAsset;
        let fromAssetName = currentFromAsset;
        let toAssetName = currentToAsset;
        const newFromAssetName = currentFromAsset
            ? oldToNewStyleTokenNames[currentFromAsset]
            : undefined;
        const newToAssetName = currentToAsset ? oldToNewStyleTokenNames[currentToAsset] : undefined;
        if (newFromAssetName &&
            currentFromAsset &&
            newFromAssetName !== currentFromAsset &&
            !assetsModule.getAsset(currentFromAsset) &&
            assetsModule.getAsset(newFromAssetName)) {
            fromAssetName = newFromAssetName;
        }
        if (newToAssetName &&
            currentToAsset &&
            newToAssetName !== currentToAsset &&
            !assetsModule.getAsset(currentToAsset) &&
            assetsModule.getAsset(newToAssetName)) {
            toAssetName = newToAssetName;
        }
        return {
            ...order,
            fromAsset: fromAssetName,
            toAsset: toAssetName,
        };
    });
};
export const parseOrder = ({ order, assets, logger, }) => {
    try {
        const needsParsing = 'version' in order ||
            (typeof order.fromAmount === 'object' && typeof order.toAmount === 'object');
        const orderForParse = order;
        return needsParsing
            ? Order.fromJSON(orderForParse)
            : orderFromJSONLegacy(orderForParse, { assets });
    }
    catch (error) {
        logger.error('Failed to parse order', order, error);
    }
};
const normalizeOrderAmounts = ({ order, assets, }) => {
    const fromAsset = order.fromAsset ? assets[order.fromAsset] : undefined;
    const toAsset = order.toAsset ? assets[order.toAsset] : undefined;
    const updates = {};
    let needsUpdate = false;
    if (order.fromAmount && fromAsset && !order.fromAmount.unitType.equals(fromAsset.currency)) {
        updates.fromAmount = fromAsset.currency.defaultUnit(order.fromAmount.toDefaultString());
        needsUpdate = true;
    }
    if (order.toAmount && toAsset && !order.toAmount.unitType.equals(toAsset.currency)) {
        updates.toAmount = toAsset.currency.defaultUnit(order.toAmount.toDefaultString());
        needsUpdate = true;
    }
    return needsUpdate ? order.update(updates) : order;
};
export const getAllOrderInstances = ({ orders, assetsModule, logger, }) => {
    try {
        const mappedOrders = mapOrdersToWalletNames({ orders, assetsModule });
        const assets = assetsModule.getAssets();
        const orderInstances = mappedOrders
            .map((order) => parseOrder({ order, assets, logger }))
            .filter(Boolean);
        const normalizedOrders = orderInstances.map((order) => normalizeOrderAmounts({ order, assets }));
        return normalizedOrders.map((order) => isOrderMetadataValid(order) ? order : convertLegacyOrderMetadata(order));
    }
    catch (err) {
        logger.error('Failed to get all order instances', err);
        return [];
    }
};
export const getOrderInstances = ({ orders, availableAssetNames, assetsModule, logger, }) => {
    const validOrders = orders.filter((order) => isOrderSupported({
        order,
        availableAssetNames,
    }));
    return getAllOrderInstances({ orders: validOrders, assetsModule, logger });
};
